import { useState, useEffect } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { blogService } from '../services/apiService';
import { getImageSrc } from '../utils/imageHelper';

const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, 4, false] }],
    [{ font: [] }],
    [{ size: ['small', false, 'large', 'huge'] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ color: [] }, { background: [] }],
    [{ align: [] }],
    [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
    ['blockquote', 'code-block'],
    ['link'],
    ['clean'],
  ],
};

const QUILL_FORMATS = [
  'header', 'font', 'size',
  'bold', 'italic', 'underline', 'strike',
  'color', 'background',
  'align',
  'list', 'indent',
  'blockquote', 'code-block',
  'link',
];

const BlogForm = ({ blog, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    videoLink: '',
    externalLink: '',
    publishDate: new Date().toISOString().split('T')[0],
    author: 'Admin',
    tags: ''
  });
  const [thumbnail, setThumbnail] = useState(null);
  const [additionalImages, setAdditionalImages] = useState([]);
  const [pdfFile, setPdfFile] = useState(null);
  const [removePdf, setRemovePdf] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (blog) {
      // Handle both old array format and new HTML string format
      let content = '';
      if (Array.isArray(blog.content)) {
        const first = blog.content[0] || '';
        // If it looks like HTML, use as-is; otherwise join paragraphs into HTML
        content = first.startsWith('<')
          ? first
          : blog.content.map(p => `<p>${p}</p>`).join('');
      } else {
        content = blog.content || '';
      }

      setFormData({
        title: blog.title,
        content,
        videoLink: blog.videoLink || '',
        externalLink: blog.externalLink || '',
        publishDate: new Date(blog.publishDate).toISOString().split('T')[0],
        author: blog.author,
        tags: blog.tags.join(', ')
      });
    }
  }, [blog]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleContentChange = (html) => {
    setFormData(prev => ({ ...prev, content: html }));
  };

  const handleThumbnailChange = (e) => setThumbnail(e.target.files[0]);

  const handleAdditionalImagesChange = (e) => {
    setAdditionalImages(Array.from(e.target.files).slice(0, 5));
  };

  const handlePdfChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type === 'application/pdf') {
      setPdfFile(file);
      setRemovePdf(false);
    }
  };

  const handleRemovePdf = () => {
    setPdfFile(null);
    setRemovePdf(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const plainText = formData.content.replace(/<[^>]*>/g, '').trim();
    if (!plainText) {
      setError('Content cannot be empty.');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const fd = new FormData();
      fd.append('title', formData.title);
      fd.append('videoLink', formData.videoLink);
      fd.append('externalLink', formData.externalLink);
      fd.append('publishDate', formData.publishDate);
      fd.append('author', formData.author);
      fd.append('tags', formData.tags);
      // Store full HTML as single content item
      fd.append('content', formData.content);

      if (thumbnail) {
        fd.append('thumbnail', thumbnail);
      } else if (!blog) {
        throw new Error('Thumbnail is required for new blogs');
      }

      additionalImages.forEach(img => fd.append('additionalImages', img));

      if (pdfFile) fd.append('pdfFile', pdfFile);
      if (removePdf) fd.append('removePdf', 'true');

      if (blog) {
        await blogService.updateBlog(blog._id, fd);
      } else {
        await blogService.createBlog(fd);
      }

      onSave();
      onClose();
    } catch (err) {
      console.error('Error:', err);
      setError(
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        'Failed to save blog. Please check all required fields.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const inputCls = 'mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-[#166534] focus:border-[#166534] text-sm';
  const labelCls = 'block text-sm font-medium text-gray-700';
  const fileCls = 'mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-[#166534] file:text-white hover:file:bg-[#0e3d1f]';

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {error && <div className="text-red-500 text-sm bg-red-50 border border-red-200 rounded p-3">{error}</div>}

      {/* Thumbnail */}
      {blog?.thumbnail && (
        <div>
          <label className={labelCls}>Current Thumbnail</label>
          <img src={getImageSrc(blog.thumbnail)} alt="Current thumbnail" className="mt-2 h-32 object-cover rounded" />
        </div>
      )}
      <div>
        <label className={labelCls}>Thumbnail {!blog && <span className="text-red-500">*</span>}</label>
        <input type="file" accept="image/*" onChange={handleThumbnailChange} className={fileCls} required={!blog} />
      </div>

      {/* Additional Images */}
      {blog?.additionalImages?.length > 0 && (
        <div>
          <label className={labelCls}>Current Additional Images</label>
          <div className="flex flex-wrap gap-2 mt-2">
            {blog.additionalImages.map((img, i) => (
              <img key={i} src={getImageSrc(img)} alt={`Additional ${i + 1}`} className="h-24 w-24 object-cover rounded" />
            ))}
          </div>
        </div>
      )}
      <div>
        <label className={labelCls}>Additional Images <span className="text-gray-400 font-normal">(max 5)</span></label>
        <input type="file" accept="image/*" multiple onChange={handleAdditionalImagesChange} className={fileCls} />
        {additionalImages.length > 0 && (
          <p className="mt-1 text-xs text-gray-500">{additionalImages.length} image(s) selected</p>
        )}
      </div>

      {/* Title */}
      <div>
        <label className={labelCls}>Title <span className="text-red-500">*</span></label>
        <input
          type="text"
          name="title"
          value={formData.title}
          onChange={handleChange}
          className={inputCls}
          required
          placeholder="Enter blog title"
        />
      </div>

      {/* WYSIWYG Content Editor */}
      <div>
        <label className={labelCls}>Content <span className="text-red-500">*</span></label>
        <div className="mt-1 rounded-md overflow-hidden border border-gray-300 focus-within:border-[#166534] focus-within:ring-1 focus-within:ring-[#166534]">
          <ReactQuill
            theme="snow"
            value={formData.content}
            onChange={handleContentChange}
            modules={QUILL_MODULES}
            formats={QUILL_FORMATS}
            placeholder="Write your blog content here..."
            style={{ minHeight: '320px' }}
          />
        </div>
      </div>

      {/* Video Link */}
      <div>
        <label className={labelCls}>Video Link <span className="text-gray-400 font-normal">(optional)</span></label>
        <input
          type="url"
          name="videoLink"
          value={formData.videoLink}
          onChange={handleChange}
          className={inputCls}
          placeholder="https://example.com/video"
        />
      </div>

      {/* External Link */}
      <div>
        <label className={labelCls}>External Link <span className="text-gray-400 font-normal">(optional)</span></label>
        <input
          type="url"
          name="externalLink"
          value={formData.externalLink}
          onChange={handleChange}
          className={inputCls}
          placeholder="https://example.com"
        />
      </div>

      {/* PDF Attachment */}
      <div>
        <label className={labelCls}>PDF Attachment <span className="text-gray-400 font-normal">(optional)</span></label>
        {blog?.pdfFile && !removePdf && (
          <div className="mt-2 flex items-center gap-3">
            <a
              href={blog.pdfFile.startsWith('/') ? `${process.env.REACT_APP_API_BASE_URL || ''}${blog.pdfFile}` : blog.pdfFile}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#166534] hover:underline text-sm"
            >
              Current PDF attached
            </a>
            <button type="button" onClick={handleRemovePdf} className="text-red-500 hover:text-red-700 text-sm">Remove</button>
          </div>
        )}
        <input type="file" accept="application/pdf" onChange={handlePdfChange} className={`${fileCls} mt-2`} />
        {pdfFile && <p className="mt-1 text-xs text-gray-500">New PDF: {pdfFile.name}</p>}
      </div>

      {/* Publish Date */}
      <div>
        <label className={labelCls}>Publish Date <span className="text-red-500">*</span></label>
        <input
          type="date"
          name="publishDate"
          value={formData.publishDate}
          onChange={handleChange}
          className={inputCls}
          required
        />
      </div>

      {/* Author */}
      <div>
        <label className={labelCls}>Author <span className="text-red-500">*</span></label>
        <input
          type="text"
          name="author"
          value={formData.author}
          onChange={handleChange}
          className={inputCls}
          required
          placeholder="Author name"
        />
      </div>

      {/* Tags */}
      <div>
        <label className={labelCls}>Tags <span className="text-red-500">*</span> <span className="text-gray-400 font-normal">(comma separated)</span></label>
        <input
          type="text"
          name="tags"
          value={formData.tags}
          onChange={handleChange}
          className={inputCls}
          required
          placeholder="sustainability, climate, net zero"
        />
      </div>

      {/* Actions */}
      <div className="flex justify-end space-x-3 pt-4 border-t border-gray-100">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isLoading}
          className="px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-[#166534] hover:bg-[#0e3d1f] disabled:opacity-50"
        >
          {isLoading ? 'Saving...' : blog ? 'Update Blog' : 'Create Blog'}
        </button>
      </div>
    </form>
  );
};

export default BlogForm;
